import {
  Directive,
  ElementRef,
  Injector,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
// GAP-FE-007 R2 · 'hover-focus' fires the tooltip on EITHER pointer hover
// OR keyboard focus, so the affordance is reachable by mouse, keyboard,
// and touch (focus is induced on touch via tap on a focusable element).
export type TooltipTrigger = 'hover' | 'click' | 'focus' | 'hover-focus';

/**
 * GAP-FE-021B · migrated from decorator @Input/ngOnChanges to signal input()
 * + effect(). Three aliased inputs (`uiTooltip`, `appTooltip`, `pTooltip`)
 * remain to preserve the original selector ergonomics.
 */
@Directive({
  selector: '[uiTooltip], [appTooltip], [pTooltip]',
  standalone: true
})
export class TooltipDirective implements OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);

  readonly uiTooltipContent = input<string>('', { alias: 'uiTooltip' });
  readonly appTooltipContent = input<string>('', { alias: 'appTooltip' });
  readonly pTooltipContent = input<string>('', { alias: 'pTooltip' });

  readonly tooltipPosition = input<TooltipPosition>('top');
  readonly tooltipTrigger = input<TooltipTrigger>('hover');
  readonly tooltipDelay = input<number>(200);
  readonly tooltipHideDelay = input<number>(0);
  readonly tooltipDisabled = input<boolean>(false);
  readonly tooltipClass = input<string>('');

  readonly content = computed<string>(() =>
    this.uiTooltipContent() || this.appTooltipContent() || this.pTooltipContent()
  );

  private tooltipElement: HTMLElement | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private isVisible = false;
  private readonly isBrowser: boolean;

  private mouseEnterListener?: () => void;
  private mouseLeaveListener?: () => void;
  private clickListener?: () => void;
  private focusListener?: () => void;
  private blurListener?: () => void;
  private documentClickListener?: (e: Event) => void;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.setupEventListeners();
    }

    effect(() => {
      const c = this.content();
      if (this.isVisible && this.tooltipElement) {
        untracked(() => this.updateTooltipContent(c));
      }
    });

    effect(() => {
      this.tooltipPosition();
      if (this.isVisible && this.tooltipElement) {
        untracked(() => this.updatePosition());
      }
    });

    effect(() => {
      this.tooltipTrigger();
      untracked(() => {
        this.removeEventListeners();
        if (this.isBrowser) {
          this.setupEventListeners();
        }
      });
    });

    // GAP-FE-A11Y-BTN · icon-only controls (e.g. icon `app-button`) often
    // expose no accessible name, so a screen reader announces only "button".
    // When this control has a tooltip, mirror that text into the focusable
    // control's `aria-label` so the tooltip becomes the accessible name. Never
    // clobber an author-provided `aria-label`/`aria-labelledby` — those win.
    //
    // NOTE: for `<app-button>` (and similar wrappers) the directive sits on the
    // CUSTOM ELEMENT host, which is `role=generic` and NOT focusable. A screen
    // reader announces the INNER native `<button>`, computing its name from the
    // inner button's own attributes — never from an ancestor generic element.
    // So we must resolve the inner focusable control and label THAT, otherwise
    // the host aria-label is inert (the original WCAG 4.1.2 finding). The inner
    // button is rendered by the child component AFTER this constructor runs, so
    // we resolve it in afterNextRender and again on every content change.
    effect(() => {
      const c = this.content();
      if (!this.isBrowser) {return;}
      untracked(() => this.syncAriaLabel(c));
    });
    if (this.isBrowser) {
      afterNextRender(() => this.syncAriaLabel(untracked(() => this.content())), {
        injector: this.injector,
      });
    }
  }

  /**
   * GAP-FE-A11Y-BTN · resolve the focusable control that actually carries the
   * accessible name. If the host is itself focusable / interactive (native
   * `<button>`, `<a href>`, `[tabindex]`, `[role]`) it is its own target.
   * Otherwise — the common `<app-button>` wrapper case — the focusable control
   * is the single inner native `<button>`/`<a>` rendered by the child view, so
   * we label that. Falls back to the host when no inner control is found.
   */
  private resolveLabelTarget(): HTMLElement | null {
    const host = this.el.nativeElement;

    const tag = host.tagName.toLowerCase();
    const hostIsFocusable =
      tag === 'button' ||
      (tag === 'a' && host.hasAttribute('href')) ||
      tag === 'input' ||
      host.hasAttribute('tabindex') ||
      host.hasAttribute('role');
    if (hostIsFocusable) {return host;}

    // Wrapper host (e.g. `<app-button>`): label the inner focusable control.
    // If it has not rendered yet, return null and let `afterNextRender` /
    // the next content change retry — never paint the inert generic host.
    return host.querySelector<HTMLElement>('button, a[href], [role="button"]');
  }

  /**
   * GAP-FE-A11Y-BTN · set the focusable target's `aria-label` from the tooltip
   * content when (a) content is non-empty and (b) that target has no existing
   * accessible name. The guard inspects the TARGET (e.g. the inner `<button>`
   * where `<app-button>`'s `[ariaLabel]` input renders) — not the generic host
   * — so an author-provided `aria-label` is genuinely respected. We only ever
   * ADD a label we own (tracked via the data attribute) and never overwrite or
   * remove an author-supplied one.
   *
   * RF-QA-194 · The aria-label mirror applies ONLY to icon-only targets (no
   * visible text). A control WITH visible text (e.g. "Validate Filtered")
   * keeps that text as its accessible name — replacing it with the whole
   * tooltip sentence broke WCAG 2.5.3 Label in Name — and instead gets the
   * tooltip as its accessible DESCRIPTION (see {@link syncAriaDescription}).
   */
  private syncAriaLabel(content: string): void {
    const target = this.resolveLabelTarget();
    if (!target) {return;}

    if (content && this.hasVisibleText(target)) {
      // RF-QA-194: visible text wins as the name; the tooltip becomes a description.
      this.releaseOwnedAriaLabel(target);
      this.syncAriaDescription(target, content);
      return;
    }
    // Icon-only (or content cleared): no description; fall through to the label mirror.
    this.releaseAriaDescription(target);

    // Content transitioned to empty (e.g. a dynamic `[appTooltip]` expression
    // that resolved to ''): remove the aria-label ONLY if we set it — the
    // `data-tooltip-aria-label` marker tracks ownership — so a stale tooltip
    // string never lingers as the control's accessible name. Author-provided
    // labels (no marker) are never touched; static-content call sites are
    // unaffected because their content is never empty.
    if (!content) {
      this.releaseOwnedAriaLabel(target);
      return;
    }

    // Author already provided an accessible name — respect it, do nothing.
    // (An existing label counts as ours only when it matches the marker value,
    // so an author label that replaced ours is also respected.)
    if (target.hasAttribute('aria-labelledby')) {return;}
    const existing = target.getAttribute('aria-label');
    if (existing && existing !== target.getAttribute('data-tooltip-aria-label')) {return;}

    this.renderer.setAttribute(target, 'aria-label', content);
    // Marker stores the value we wrote so the removal path can tell our label
    // apart from a later author-provided one.
    this.renderer.setAttribute(target, 'data-tooltip-aria-label', content);
  }

  /**
   * RF-QA-194 · true when the target exposes a visible text LABEL of its own.
   * Requires at least one letter: a bare counter or symbol ("0/4", "+", "…")
   * is not a name, so such controls keep the tooltip as their accessible name.
   */
  private hasVisibleText(target: HTMLElement): boolean {
    return /\p{L}/u.test(target.textContent ?? '');
  }

  /** Remove an aria-label ONLY if this directive wrote it (marker-tracked). */
  private releaseOwnedAriaLabel(target: HTMLElement): void {
    if (!target.hasAttribute('data-tooltip-aria-label')) {return;}
    // Only remove the aria-label if it still holds the exact value we wrote
    // (stored in the marker) — if an author label replaced ours in the
    // meantime, drop only our ownership marker and leave theirs.
    if (target.getAttribute('aria-label') === target.getAttribute('data-tooltip-aria-label')) {
      this.renderer.removeAttribute(target, 'aria-label');
    }
    this.renderer.removeAttribute(target, 'data-tooltip-aria-label');
  }

  /**
   * RF-QA-194 · Expose the tooltip as the accessible DESCRIPTION of a
   * text-labelled target through `aria-description` on the target itself.
   * Deliberately NOT an `aria-describedby` reference to an extra element: a
   * second, hidden copy of the text in <body> duplicated visible copy (e.g. a
   * helper paragraph that repeats the tooltip) for every exact-text lookup —
   * Playwright `getByText` strict mode, find-in-page, assistive-tech browse
   * mode. An author-provided `aria-describedby` still takes precedence in the
   * accessible-description computation; an author `aria-description` is never
   * overwritten (ownership is marker-tracked, as for aria-label).
   */
  private syncAriaDescription(target: HTMLElement, content: string): void {
    const existing = target.getAttribute('aria-description');
    const ours = target.getAttribute('data-tooltip-aria-description');
    if (existing !== null && existing !== ours) {return;}
    this.renderer.setAttribute(target, 'aria-description', content);
    this.renderer.setAttribute(target, 'data-tooltip-aria-description', content);
  }

  /** RF-QA-194 · Remove an aria-description ONLY if this directive wrote it. */
  private releaseAriaDescription(target: HTMLElement | null): void {
    if (!target?.hasAttribute('data-tooltip-aria-description')) {return;}
    if (target.getAttribute('aria-description') === target.getAttribute('data-tooltip-aria-description')) {
      this.renderer.removeAttribute(target, 'aria-description');
    }
    this.renderer.removeAttribute(target, 'data-tooltip-aria-description');
  }

  ngOnDestroy(): void {
    this.hide();
    this.removeEventListeners();
    this.clearTimeouts();
  }

  private setupEventListeners(): void {
    if (!this.isBrowser) {return;}

    const element = this.el.nativeElement;

    switch (this.tooltipTrigger()) {
      case 'hover':
        this.mouseEnterListener = this.renderer.listen(element, 'mouseenter', () => this.scheduleShow());
        this.mouseLeaveListener = this.renderer.listen(element, 'mouseleave', () => this.scheduleHide());
        break;
      case 'click':
        this.clickListener = this.renderer.listen(element, 'click', (e: Event) => {
          e.stopPropagation();
          this.toggle();
        });
        this.documentClickListener = (e: Event) => {
          if (!element.contains(e.target as Node) && this.tooltipElement && !this.tooltipElement.contains(e.target as Node)) {
            this.hide();
          }
        };
        this.document.addEventListener('click', this.documentClickListener);
        break;
      case 'focus':
        this.focusListener = this.renderer.listen(element, 'focus', () => this.scheduleShow());
        this.blurListener = this.renderer.listen(element, 'blur', () => this.scheduleHide());
        break;
      case 'hover-focus':
        // GAP-FE-007 R2 · combined hover + focus trigger. Mouse users get
        // the standard hover behavior; keyboard / touch users get focus-
        // driven activation. Both hide on the matching leave/blur event.
        this.mouseEnterListener = this.renderer.listen(element, 'mouseenter', () => this.scheduleShow());
        this.mouseLeaveListener = this.renderer.listen(element, 'mouseleave', () => this.scheduleHide());
        this.focusListener = this.renderer.listen(element, 'focus', () => this.scheduleShow());
        this.blurListener = this.renderer.listen(element, 'blur', () => this.scheduleHide());
        break;
    }
  }

  private removeEventListeners(): void {
    if (typeof this.mouseEnterListener === 'function') {
      this.mouseEnterListener();
      this.mouseEnterListener = undefined;
    }
    if (typeof this.mouseLeaveListener === 'function') {
      this.mouseLeaveListener();
      this.mouseLeaveListener = undefined;
    }
    if (typeof this.clickListener === 'function') {
      this.clickListener();
      this.clickListener = undefined;
    }
    if (typeof this.focusListener === 'function') {
      this.focusListener();
      this.focusListener = undefined;
    }
    if (typeof this.blurListener === 'function') {
      this.blurListener();
      this.blurListener = undefined;
    }
    if (this.documentClickListener) {
      this.document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = undefined;
    }
  }

  private scheduleShow(): void {
    if (this.tooltipDisabled() || !this.content()) {return;}

    // GAP-FE-A11Y-BTN · safety net — by the time the control is hovered/focused
    // the inner focusable element is guaranteed to exist, so re-sync the
    // accessible name here in case it had not rendered when the effect ran.
    this.syncAriaLabel(this.content());

    this.clearTimeouts();
    this.showTimeout = setTimeout(() => this.show(), this.tooltipDelay());
  }

  private scheduleHide(): void {
    this.clearTimeouts();
    this.hideTimeout = setTimeout(() => this.hide(), this.tooltipHideDelay());
  }

  private clearTimeouts(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  private show(): void {
    if (this.tooltipDisabled() || !this.content() || !this.isBrowser) {return;}

    this.createTooltip();
    this.updatePosition();
    this.isVisible = true;

    requestAnimationFrame(() => {
      if (this.tooltipElement) {
        this.renderer.addClass(this.tooltipElement, 'opacity-100');
        this.renderer.removeClass(this.tooltipElement, 'opacity-0');
      }
    });
  }

  private hide(): void {
    if (!this.tooltipElement) {return;}

    this.renderer.addClass(this.tooltipElement, 'opacity-0');
    this.renderer.removeClass(this.tooltipElement, 'opacity-100');

    setTimeout(() => {
      this.removeTooltip();
      this.isVisible = false;
    }, 150);
  }

  private createTooltip(): void {
    if (this.tooltipElement) {return;}

    this.tooltipElement = this.renderer.createElement('div');

    const baseClasses = [
      'fixed',
      'z-tooltip',
      'px-2',
      'py-1',
      'text-xs',
      'font-medium',
      'text-white',
      'bg-bb-bg-tertiary',
      'border',
      'border-bb-border',
      'rounded-md',
      'shadow-lg',
      'transition-opacity',
      'duration-150',
      'opacity-0',
      'pointer-events-none',
      'max-w-xs',
      'break-words'
    ];

    baseClasses.forEach(cls => this.renderer.addClass(this.tooltipElement, cls));

    const tc = this.tooltipClass();
    if (tc) {
      tc.split(' ').filter(Boolean).forEach(cls => {
        this.renderer.addClass(this.tooltipElement, cls);
      });
    }

    this.renderer.setProperty(this.tooltipElement, 'textContent', this.content());

    const arrow = this.createArrow();
    this.renderer.appendChild(this.tooltipElement, arrow);

    this.renderer.appendChild(this.document.body, this.tooltipElement);
  }

  private createArrow(): HTMLElement {
    const arrow = this.renderer.createElement('div');

    const arrowClasses = [
      'absolute',
      'w-2',
      'h-2',
      'bg-bb-bg-tertiary',
      'border',
      'border-bb-border',
      'transform',
      'rotate-45'
    ];

    arrowClasses.forEach(cls => this.renderer.addClass(arrow, cls));

    switch (this.tooltipPosition()) {
      case 'top':
        this.renderer.addClass(arrow, '-bottom-1');
        this.renderer.addClass(arrow, 'left-1/2');
        this.renderer.addClass(arrow, '-translate-x-1/2');
        this.renderer.setStyle(arrow, 'border-top', 'none');
        this.renderer.setStyle(arrow, 'border-left', 'none');
        break;
      case 'bottom':
        this.renderer.addClass(arrow, '-top-1');
        this.renderer.addClass(arrow, 'left-1/2');
        this.renderer.addClass(arrow, '-translate-x-1/2');
        this.renderer.setStyle(arrow, 'border-bottom', 'none');
        this.renderer.setStyle(arrow, 'border-right', 'none');
        break;
      case 'left':
        this.renderer.addClass(arrow, 'top-1/2');
        this.renderer.addClass(arrow, '-right-1');
        this.renderer.addClass(arrow, '-translate-y-1/2');
        this.renderer.setStyle(arrow, 'border-left', 'none');
        this.renderer.setStyle(arrow, 'border-bottom', 'none');
        break;
      case 'right':
        this.renderer.addClass(arrow, 'top-1/2');
        this.renderer.addClass(arrow, '-left-1');
        this.renderer.addClass(arrow, '-translate-y-1/2');
        this.renderer.setStyle(arrow, 'border-right', 'none');
        this.renderer.setStyle(arrow, 'border-top', 'none');
        break;
    }

    return arrow;
  }

  private removeTooltip(): void {
    if (this.tooltipElement && this.tooltipElement.parentNode) {
      this.renderer.removeChild(this.document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  private updateTooltipContent(text: string): void {
    if (this.tooltipElement) {
      this.tooltipElement.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = text;
        }
      });
      if (!Array.from(this.tooltipElement.childNodes).some(n => n.nodeType === Node.TEXT_NODE)) {
        this.removeTooltip();
        this.createTooltip();
      }
    }
  }

  private updatePosition(): void {
    if (!this.tooltipElement || !this.isBrowser) {return;}

    const hostRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const offset = 8;

    let top: number;
    let left: number;

    switch (this.tooltipPosition()) {
      case 'top':
        top = hostRect.top - tooltipRect.height - offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + offset;
        break;
    }

    const viewportWidth = this.document.defaultView?.innerWidth ?? 0;
    const viewportHeight = this.document.defaultView?.innerHeight ?? 0;

    if (left < 8) {
      left = 8;
    } else if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }

    if (top < 8) {
      top = 8;
    } else if (top + tooltipRect.height > viewportHeight - 8) {
      top = viewportHeight - tooltipRect.height - 8;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  }
}
